import { useMemo } from "react";

import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useUser } from "@/api/hooks/useUser";
import { HouseholdData } from "@/api/rest/models/household";

import { CaseType } from "@/api/rest/models/warranty";
import { getStructuredWarrantyRules } from "@/api/rest/services/warrantyService";
import { getAvailableHouseholdAccounts } from "@/utils/householdAccounts";

import ChatSubmission from "./ChatSubmission";
import DirectSubmissionForm from "./DirectSubmissionForm";
import StructuredWarrantyChat from "./StructuredWarrantyChat";

interface NewClaimProps {
  householdData: HouseholdData;
  refreshCases: () => void;
  caseType: CaseType;
}

const NewClaim: React.FC<NewClaimProps> = ({ householdData, refreshCases, caseType }) => {
  const user = useUser();
  const { t } = useTranslation();

  const householdId = householdData.household.id;
  const { data: structuredWarrantyRules } = useQuery({
    queryKey: ["structured-warranty-rules", householdId],
    queryFn: async () => {
      const response = await getStructuredWarrantyRules(householdId);
      if (response.success) {
        return response.result;
      }
      return null;
    },
    enabled: householdId > 0,
  });

  const isStructuredWarrantyEnabled = structuredWarrantyRules?.enabled === true && caseType === CaseType.WARRANTY;

  const isManagerHousehold = useMemo(
    () => householdData.featureFlags?.isManagerHousehold === true,
    [householdData.featureFlags],
  );

  const warrantySubmissionDisabled =
    caseType === CaseType.WARRANTY && householdData.featureFlags?.warrantySubmissionDisabled === true;

  const warrantyDisabledMessage =
    (householdData.featureFlags?.warrantyDisabledMessage as string) || t("warranty.submissionDisabled");

  const availableAccounts = useMemo(() => {
    return getAvailableHouseholdAccounts(user, householdData);
  }, [householdData, user]);

  return (
    <div className="flex flex-grow basis-1/2 flex-col gap-4">
      {warrantySubmissionDisabled ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <InformationCircleIcon className="h-10 w-10 text-slate-400" />
          <p className="text-sm leading-relaxed text-slate-600 whitespace-pre-line text-center">
            {warrantyDisabledMessage}
          </p>
        </div>
      ) : caseType === CaseType.WARRANTY && isManagerHousehold ? (
        <DirectSubmissionForm
          householdData={householdData}
          availableAccounts={availableAccounts}
          refreshCases={refreshCases}
        />
      ) : isStructuredWarrantyEnabled && structuredWarrantyRules ? (
        <StructuredWarrantyChat
          householdData={householdData}
          availableAccounts={availableAccounts}
          refreshCases={refreshCases}
          rules={structuredWarrantyRules}
        />
      ) : (
        <ChatSubmission
          householdData={householdData}
          availableAccounts={availableAccounts}
          refreshCases={refreshCases}
          caseType={caseType}
        />
      )}
    </div>
  );
};

export default NewClaim;
